import { EditOutlined } from '@mui/icons-material'
import { Box, styled, useMediaQuery } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  invalidateComponentsCache,
  useBulkUpdateBatchNumber,
  useGetComponentsOfProductInMooring,
} from 'src/api/components'
import { invalidatePositionCache } from 'src/api/positions'
import { BatchNumberCell } from 'src/components/batch-numbers/BatchNumberCell'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { CenteredSpinner } from 'src/components/Spinner'
import { IComponent, IComponentFilters, IPosition } from 'src/generated-types'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { CustomStyledTable } from 'src/screens/localities/moorings/Components/PositionTable'
import { UpdateBatchNumberDialog } from 'src/screens/localities/moorings/UpdateBatchNumberDialog'
import { getFormattedDate } from 'src/utils'

interface IComponentsTableProps {
  customerId: EntityId
  localityId: EntityId
  mooringId: EntityId
  productId: EntityId
  localityName?: string
  positionName?: string
  filters: IComponentFilters
  isBatchNumberEditable?: boolean
}
const ComponentsTable: React.FC<IComponentsTableProps> = ({
  customerId,
  localityId,
  mooringId,
  productId,
  localityName,
  filters,
  isBatchNumberEditable = false,
}) => {
  const isMobile = useMediaQuery('(max-width: 1024px)')
  const { t } = useTranslation(['component', 'common'])
  const { showDialog, openFilePreviewDialog } = useDialogCtx()
  const { mutateAsync: updateBatchNumbers } = useBulkUpdateBatchNumber(customerId)
  const { data: components, isLoading } = useGetComponentsOfProductInMooring(
    customerId,
    localityId,
    mooringId,
    productId,
    filters,
  )

  const getRemainingLifespan = (installationDate: Date, expectedLifetime: number) => {
    const currentDate = new Date()
    const installationDateOfComponent = installationDate ? new Date(installationDate) : null
    if (!installationDateOfComponent) return -1
    const endOfLifeDate = new Date(
      installationDateOfComponent.setMonth(installationDateOfComponent.getMonth() + expectedLifetime),
    )

    const remainingLife =
      (endOfLifeDate.getFullYear() - currentDate.getFullYear()) * 12 + endOfLifeDate.getMonth() - currentDate.getMonth()
    return remainingLife >= 0 ? remainingLife : 0
  }

  const getPositionName = (position: IPosition) => {
    return position.reference ? position.reference : position.name
  }

  const refreshBatchNumbers = () => {
    invalidateComponentsCache.getComponentsInMooring(customerId)
    invalidatePositionCache.getBatchNumberDetailsByProductId(customerId, localityId, mooringId)
  }

  const onAddSingleBatchNumber = async (form: any) => {
    try {
      await updateBatchNumbers(form)
      refreshBatchNumbers()
      toast.success(t('successfullyUpdated', { ns: 'common-api-message' }))
    } catch {
      toast.error(t('InternalError', { ns: 'common-api-message' }))
    }
  }

  // Same dialog as on the locality's mooring: pick the positions of this product and set common batch numbers.
  // The dialog invalidates the affected component lists itself after a successful update.
  const onUpdateBatchNumbers = (component: IComponent) => {
    showDialog(
      UpdateBatchNumberDialog,
      {
        opts: { maxWidth: 'sm', fullWidth: true },
        componentProps: {
          customerId,
          localityId,
          mooringId,
          productId,
          positionId: component.positionId,
          componentId: component.id,
          productDescription: component.productDescription,
        },
      },
      undefined,
      true,
    )
  }

  const columns: GridColumns = [
    {
      field: '',
      headerName: '',
      width: 50,
      hide: isMobile,
    },
    {
      field: 'localityName',
      headerName: t('localityName', { ns: 'component' }),
      flex: 1,
      hide: isMobile,
      renderCell: params => {
        return <DynamicTextTypography text={localityName ?? ''} maxLength={15} fontWeight={400} />
      },
    },
    {
      field: 'positionName',
      headerName: t('positionName', { ns: 'component' }),
      flex: 1,
      minWidth: 100,
      renderCell: params => {
        return (
          <DynamicTextTypography
            paddingLeft={2}
            text={getPositionName(params.row.position)}
            maxLength={isMobile ? 8 : 10}
            fontWeight={500}
          />
        )
      },
    },
    {
      field: 'productNumber',
      headerName: t('articleNumber', { ns: 'component' }),
      flex: 1.5,
      minWidth: 100,
      hide: isMobile,
      renderCell: params => {
        return (
          <DynamicTextTypography
            paddingLeft={2}
            text={params.row?.productNumber}
            maxLength={isMobile ? 15 : 25}
            fontWeight={400}
          />
        )
      },
    },
    {
      field: 'productDescription',
      headerName: t('productDescription', { ns: 'component' }),
      flex: 1.5,
      hide: isMobile,
      renderCell: params => {
        return <DynamicTextTypography paddingLeft={2} text={params.value} maxLength={25} fontWeight={400} />
      },
    },
    {
      field: 'quantity',
      headerName: t('quantity', { ns: 'component' }),
      minWidth: 60,
      flex: 1,
      renderCell: params => {
        return <Box paddingLeft={isMobile ? 1 : 3}>{params.value}</Box>
      },
    },
    {
      field: 'certificationFiles',
      headerName: t('batchNumber', { ns: 'component' }),
      flex: 1.5,
      minWidth: 100,
      renderCell: params => {
        return (
          <StyledBatchNumberContainer>
            <BatchNumberCell
              positionId={params?.row?.positionId as number}
              componentId={params?.row?.id as number}
              certificationFiles={params?.row?.certificationFiles}
              onClick={isBatchNumberEditable ? openFilePreviewDialog : () => {}}
              onAddSingleBatchNumber={isBatchNumberEditable ? onAddSingleBatchNumber : () => {}}
              disabledAddBatchNumber={!isBatchNumberEditable}
            />
          </StyledBatchNumberContainer>
        )
      },
    },
    {
      field: 'installationDate',
      headerName: t('installationDate', { ns: 'component' }),
      minWidth: 120,
      flex: 1.2,
      type: 'date',
      editable: true,
      renderCell: ({ value }) => {
        return <Box paddingLeft={2}>{value ? getFormattedDate(value) : '-'}</Box>
      },
    },
    {
      field: 'designatedLifeTime',
      headerName: t('designatedLifeTime', { ns: 'component' }),
      flex: 1,
      minWidth: 120,
      renderCell: params => {
        const remainingTime = getRemainingLifespan(
          params.row.installationDate,
          params.row.product?.expectedLifetime ?? 0,
        )
        return (
          <Box paddingLeft={3}>
            {remainingTime > 0
              ? remainingTime + ' ' + t('months')
              : remainingTime == -1
              ? '-'
              : t('expired', { ns: 'common' })}
          </Box>
        )
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      hide: !isBatchNumberEditable,
      renderCell: params => (
        <GridActionButton
          buttonIcon={<EditOutlined fontSize="small" />}
          toolTipTitle={t('update-batch-numbers', { ns: 'projects' })}
          aria-label={t('update-batch-numbers', { ns: 'projects' })}
          onClick={() => onUpdateBatchNumbers(params.row as IComponent)}
          variant="outlined"
          shadowWeight={0}
          height="27px"
          width="27px"
        />
      ),
    },
  ]
  return isLoading ? (
    <CenteredSpinner size={'50px'} height={100} />
  ) : (
    <Box sx={{ width: '100%', overflowX: isMobile ? 'auto' : 'visible' }}>
      <CustomStyledTable id="components-table" columns={columns} data={components} getRowHeight={() => 44} />
    </Box>
  )
}

export default ComponentsTable

const StyledBatchNumberContainer = styled(Box)({
  paddingLeft: '8px',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  '& .batch-number': {
    minWidth: '60px',
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: '#f5f5f5',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 500,
  },
})
